(function () {
// ─── Utilities ──────────────────────────────────────────────────────────
const fmt = (cents) =>
  `€${((+cents || 0) / 100).toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const DutchXXLTheme = (window.DutchXXLTheme = window.DutchXXLTheme || {});

const $ = (selector, context = document) => context.querySelector(selector);

const $$ = (selector, context = document) => [
  ...context.querySelectorAll(selector),
];

const WISHLIST_STORAGE_KEY = "dutchxxl_wishlist";
const MAX_WISHLIST_ITEMS = 60;
const WISHLIST_HANDLE_PATTERN = /^[a-z0-9][a-z0-9-]{0,254}$/;

const storedWishlistCount = () => {
  try {
    const data = JSON.parse(
      window.localStorage.getItem(WISHLIST_STORAGE_KEY) || "null",
    );
    if (!data || typeof data !== "object") return 0;

    const entries = Array.isArray(data)
      ? data.map((item) => [item?.handle, item])
      : Object.entries(data);
    const handles = new Set();

    for (const [key, value] of entries) {
      if (!value || typeof value !== "object") continue;
      const handle = String(value.handle || key || "").trim().toLowerCase();
      if (
        !WISHLIST_HANDLE_PATTERN.test(handle) ||
        !String(value.title || "").trim()
      ) {
        continue;
      }

      handles.add(handle);
      if (handles.size >= MAX_WISHLIST_ITEMS) break;
    }

    return handles.size;
  } catch {
    return 0;
  }
};

const syncWishlistCount = (count = storedWishlistCount()) => {
  const parsedCount = Number(count);
  const safeCount = Number.isFinite(parsedCount)
    ? Math.min(Math.max(Math.trunc(parsedCount), 0), MAX_WISHLIST_ITEMS)
    : storedWishlistCount();

  $$('[data-wishlist-count]').forEach((badge) => {
    badge.textContent = safeCount;
    badge.classList.toggle("show", safeCount > 0);
  });
};

const initializeWishlistCount = () => {
  syncWishlistCount();
  window.addEventListener("storage", (event) => {
    if (event.key === WISHLIST_STORAGE_KEY || event.key === null) {
      syncWishlistCount();
    }
  });
  document.addEventListener("wishlist:changed", (event) => {
    syncWishlistCount(event.detail?.count);
  });
  document.addEventListener("shopify:section:load", (event) => {
    if (event.target?.querySelector?.('[data-wishlist-count]')) {
      syncWishlistCount();
    }
  });
};

const el = (tag, className) =>
  Object.assign(document.createElement(tag), { className });

const scrollTopFromBodyLock = (fallback = 0) => {
  const top = parseInt(document.body.style.top || "0", 10);
  const scrollTop = Number.isNaN(top) ? fallback : top * -1;
  return Math.max(scrollTop || fallback, 0);
};

const restoreViewportScroll = (scrollTop) => {
  const root = document.documentElement;
  const previousScrollBehavior = root.style.scrollBehavior;
  root.style.scrollBehavior = "auto";
  scrollTo(0, Math.max(scrollTop, 0));
  root.style.scrollBehavior = previousScrollBehavior;
};

const afterElementMotion = (element, callback) => {
  const finish = () => callback?.();
  requestAnimationFrame(() => {
    const animations = element?.getAnimations?.().filter(
      (animation) => animation.playState !== "finished",
    );
    if (!animations?.length) {
      finish();
      return;
    }
    Promise.allSettled(animations.map((animation) => animation.finished)).then(
      finish,
    );
  });
};

const escapeHtml = (value) => {
  const div = document.createElement("div");
  div.textContent = value || "";
  return div.innerHTML;
};

const cartRoot = () => window.Shopify?.routes?.root || "/";

const cartEndpoint = (endpoint) => {
  const root = cartRoot().replace(/\/?$/, "/");
  return endpoint.startsWith(".")
    ? `${root}cart${endpoint}`
    : `${root}cart/${endpoint}`;
};

const currentSectionsUrl = () =>
  `${window.location.pathname}${window.location.search}` || "/";

// Shopify applies semantic understanding to full search, but not predictive
// search. On this catalog that can omit literal product-family matches. Keep
// the compatibility query internal so the customer-facing query stays plain.
const STOREFRONT_SEARCH_SYNTAX =
  /(^|\s)(?:AND|OR)(?=\s|$)|(^|\s)-\S|(^|\s)(?:body|product_type|tag|title|variants\.(?:barcode|sku|title)|vendor):\S|["()]/;
const AUTO_EXACT_SEARCH_QUERY = /^"([^"\r\n]+)"$/;

const toExactSearchQuery = (value) => {
  const query = String(value || "").trim();
  if (!query || STOREFRONT_SEARCH_SYNTAX.test(query)) return query;
  return `"${query}"`;
};

const decodeSearchEntities = (value) =>
  String(value || "")
    .replace(/&amp;(quot|#0*34|apos|#0*39);/gi, "&$1;")
    .replace(/&quot;|&#0*34;/gi, '"')
    .replace(/&apos;|&#0*39;/gi, "'");

const toPublicSearchQuery = (value) => {
  const query = decodeSearchEntities(value).trim();
  const exactMatch = query.match(AUTO_EXACT_SEARCH_QUERY);
  return exactMatch ? exactMatch[1].trim() : query;
};

const storefrontSearchUrl = () => {
  const root = (window.Shopify?.routes?.root || "/").replace(/\/?$/, "/");
  return new URL(`${root}search`, window.location.origin);
};

const isStorefrontSearchUrl = (value) => {
  try {
    const url =
      value instanceof URL ? value : new URL(value, window.location.href);
    const searchUrl = storefrontSearchUrl();
    return url.origin === searchUrl.origin && url.pathname === searchUrl.pathname;
  } catch {
    return false;
  }
};

const publicSearchUrlValue = (value) => {
  try {
    const url = new URL(value, window.location.href);
    if (!isStorefrontSearchUrl(url) || !url.searchParams.has("q")) {
      return value;
    }

    url.searchParams.set(
      "q",
      toPublicSearchQuery(url.searchParams.get("q")),
    );
    return url.toString();
  } catch {
    return value;
  }
};

const publicFullSearchUrl = (value, query) => {
  const url = value instanceof URL
    ? new URL(value.toString())
    : new URL(value || storefrontSearchUrl(), window.location.href);
  const publicQuery = toPublicSearchQuery(query ?? url.searchParams.get("q"));

  if (publicQuery) {
    url.searchParams.set("q", publicQuery);
  } else {
    url.searchParams.delete("q");
  }
  url.hash = "";
  return { url, publicQuery };
};

const navigateToFullSearch = (value, query) => {
  const { url, publicQuery } = publicFullSearchUrl(value, query);
  if (!publicQuery) return;

  const exactQuery = toExactSearchQuery(publicQuery);
  if (exactQuery === publicQuery) {
    window.location.assign(url.toString());
    return;
  }

  // Shopify gives POST body parameters precedence over the action query
  // string. The server receives the deterministic phrase query, while the
  // address bar and canonical URL retain the customer's plain query.
  const requestForm = document.createElement("form");
  const queryInput = document.createElement("input");
  requestForm.method = "post";
  requestForm.action = url.toString();
  requestForm.hidden = true;
  requestForm.setAttribute("aria-hidden", "true");
  queryInput.type = "hidden";
  queryInput.name = "q";
  queryInput.value = exactQuery;
  requestForm.appendChild(queryInput);
  document.body.appendChild(requestForm);
  HTMLFormElement.prototype.submit.call(requestForm);
};

const replaceSearchText = (value, exactQuery, publicQuery) =>
  decodeSearchEntities(value).split(exactQuery).join(publicQuery);

const normalizeVisibleFullSearch = (publicQuery, exactQuery) => {
  if (!publicQuery || !exactQuery) return;

  $$('input[name="q"]').forEach((input) => {
    if (toPublicSearchQuery(input.value) === publicQuery) {
      input.value = publicQuery;
    }
  });

  document.title = replaceSearchText(document.title, exactQuery, publicQuery);
  [
    'meta[property="og:title"]',
    'meta[name="twitter:title"]',
    'meta[name="description"]',
    'meta[property="og:description"]',
    'meta[name="twitter:description"]',
  ].forEach((selector) => {
    const element = $(selector);
    if (!element?.content) return;
    element.content = replaceSearchText(element.content, exactQuery, publicQuery);
  });

  const canonical = $('link[rel="canonical"]');
  if (canonical?.href) canonical.href = publicSearchUrlValue(canonical.href);

  const openGraphUrl = $('meta[property="og:url"]');
  if (openGraphUrl?.content) {
    openGraphUrl.content = publicSearchUrlValue(openGraphUrl.content);
  }

  $$('a[href]').forEach((link) => {
    let url;
    try {
      url = new URL(link.href, window.location.href);
    } catch {
      return;
    }
    if (!isStorefrontSearchUrl(url) || !url.searchParams.has("q")) return;

    const linkQuery = url.searchParams.get("q");
    const publicLinkQuery = toPublicSearchQuery(linkQuery);
    if (linkQuery === publicLinkQuery) return;

    url.searchParams.set("q", publicLinkQuery);
    link.setAttribute(
      "href",
      url.origin === window.location.origin
        ? `${url.pathname}${url.search}${url.hash}`
        : url.toString(),
    );
  });
};

const replaceFullSearchMetadata = (nextDocument, exactQuery, publicQuery) => {
  document.title = replaceSearchText(
    nextDocument.title,
    exactQuery,
    publicQuery,
  );

  [
    'meta[property="og:title"]',
    'meta[name="twitter:title"]',
    'meta[name="description"]',
    'meta[property="og:description"]',
    'meta[name="twitter:description"]',
    'meta[property="og:url"]',
    'link[rel="canonical"]',
  ].forEach((selector) => {
    const currentElement = $(selector);
    const nextElement = nextDocument.querySelector(selector);
    if (!currentElement || !nextElement) return;

    if (currentElement instanceof HTMLLinkElement) {
      currentElement.href = publicSearchUrlValue(nextElement.href);
      return;
    }

    currentElement.content =
      selector === 'meta[property="og:url"]'
        ? publicSearchUrlValue(nextElement.content)
        : replaceSearchText(nextElement.content, exactQuery, publicQuery);
  });
};

const refreshPlainFullSearch = async (
  publicUrl,
  publicQuery,
  exactQuery,
  currentSearchRoot,
) => {
  currentSearchRoot.setAttribute("aria-busy", "true");

  try {
    const body = new URLSearchParams({ q: exactQuery });
    const response = await fetch(publicUrl.toString(), {
      method: "POST",
      headers: {
        Accept: "text/html",
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
      },
      body,
      credentials: "same-origin",
      cache: "no-store",
    });
    if (!response.ok) throw new Error("Full search refresh failed");

    const html = await response.text();
    const nextDocument = new DOMParser().parseFromString(html, "text/html");
    const nextSearchRoot = nextDocument.querySelector(
      "[data-search-server-terms]",
    );
    if (
      decodeSearchEntities(nextSearchRoot?.dataset.searchServerTerms).trim() !==
      exactQuery
    ) {
      throw new Error("Full search refresh returned the wrong query mode");
    }

    const currentSection = currentSearchRoot.closest('[id^="shopify-section-"]');
    const nextSection = currentSection?.id
      ? nextDocument.getElementById(currentSection.id)
      : null;

    if (currentSection && nextSection) {
      currentSection.replaceWith(document.importNode(nextSection, true));
    } else {
      currentSearchRoot.replaceWith(document.importNode(nextSearchRoot, true));
    }

    replaceFullSearchMetadata(nextDocument, exactQuery, publicQuery);
    normalizeVisibleFullSearch(publicQuery, exactQuery);
    history.replaceState(history.state, "", publicUrl.toString());
  } catch (error) {
    currentSearchRoot.removeAttribute("aria-busy");
    console.warn("Dutch XXL full search compatibility refresh failed", error);
  }
};

const initializeFullSearchPage = () => {
  const searchRoot = $("[data-search-server-terms]");
  if (!searchRoot || !isStorefrontSearchUrl(window.location.href)) {
    return Promise.resolve();
  }

  const { url: publicUrl, publicQuery } = publicFullSearchUrl(
    window.location.href,
  );
  if (!publicQuery) return Promise.resolve();

  const exactQuery = toExactSearchQuery(publicQuery);
  const serverQuery = decodeSearchEntities(
    searchRoot.dataset.searchServerTerms,
  ).trim();

  normalizeVisibleFullSearch(publicQuery, exactQuery);
  history.replaceState(history.state, "", publicUrl.toString());

  if (serverQuery === exactQuery || exactQuery === publicQuery) {
    return Promise.resolve();
  }

  return refreshPlainFullSearch(
    publicUrl,
    publicQuery,
    exactQuery,
    searchRoot,
  );
};

const bindFullSearchQueries = () => {
  document.addEventListener(
    "submit",
    (event) => {
      const form = event.target.closest?.("[data-full-search-form]");
      if (!form) return;

      const input = form.elements?.namedItem("q");
      if (!(input instanceof HTMLInputElement) || !input.value.trim()) return;

      event.preventDefault();
      const formData = new FormData(form);
      const url = new URL(
        form.action || storefrontSearchUrl(),
        window.location.href,
      );
      const parameterNames = new Set(formData.keys());
      parameterNames.forEach((name) => url.searchParams.delete(name));

      for (const [name, value] of formData) {
        if (name === "q" || typeof value !== "string" || !value) continue;
        url.searchParams.append(name, value);
      }

      navigateToFullSearch(url, input.value);
    },
    true,
  );

  document.addEventListener(
    "click",
    (event) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const link = event.target.closest?.('a[href]');
      if (
        !link ||
        link.hasAttribute("download") ||
        (link.target && link.target !== "_self") ||
        !isStorefrontSearchUrl(link.href)
      ) {
        return;
      }

      const url = new URL(link.href, window.location.href);
      if (!url.searchParams.get("q")) return;

      event.preventDefault();
      navigateToFullSearch(url);
    },
    true,
  );
};

const cartSectionId = () => $("[data-cart-drawer]")?.dataset.cartSectionId || "";

const withCartSections = (body = {}) => {
  const sectionId = cartSectionId();
  if (!sectionId) return body;
  return {
    ...body,
    sections: [sectionId],
    sections_url: currentSectionsUrl(),
  };
};

const fetchCart = async () => {
  const response = await fetch(cartEndpoint(".js"), {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw new Error("Cart request failed");
  return response.json();
};

const postCart = async (endpoint, body, includeSections = true) => {
  const response = await fetch(cartEndpoint(endpoint), {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(includeSections ? withCartSections(body) : body),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(data?.description || data?.message || "Cart request failed");
  }
  return data;
};

const syncCartCountValue = (count) => {
  const safeCount = Number(count) || 0;
  $$("[data-cart-count]").forEach((badge) => {
    badge.textContent = safeCount;
    badge.classList.toggle("show", safeCount > 0);
    badge.style.display = "";
  });
};

const syncCartCounts = (cart) => {
  syncCartCountValue(cart?.item_count);
};

DutchXXLTheme.formatMoney = fmt;
DutchXXLTheme.postCartJson = postCart;
DutchXXLTheme.syncCartCounts = syncCartCounts;
DutchXXLTheme.syncWishlistCount = syncWishlistCount;
DutchXXLTheme.toExactSearchQuery = toExactSearchQuery;
DutchXXLTheme.toPublicSearchQuery = toPublicSearchQuery;
DutchXXLTheme.navigateToFullSearch = navigateToFullSearch;

bindFullSearchQueries();
DutchXXLTheme.fullSearchReady = initializeFullSearchPage();

const loadStylesheet = (href) =>
  new Promise((resolve) => {
    if (!href) {
      resolve();
      return;
    }

    const existing = document.querySelector(`link[href="${href}"]`);
    if (existing) {
      if (
        existing.dataset.loadState === "loaded" ||
        existing.dataset.loadState === "error" ||
        existing.sheet
      ) {
        resolve();
        return;
      }
      existing.addEventListener("load", resolve, { once: true });
      existing.addEventListener("error", resolve, { once: true });
      return;
    }

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.dataset.loadState = "loading";
    link.onload = () => {
      link.dataset.loadState = "loaded";
      resolve();
    };
    link.onerror = () => {
      link.dataset.loadState = "error";
      resolve();
    };
    document.head.appendChild(link);
  });

const loadScriptOnce = (src) =>
  new Promise((resolve) => {
    if (!src) {
      resolve();
      return;
    }

    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loadState === "loaded" || existing.dataset.loadState === "error") {
        resolve();
        return;
      }
      existing.addEventListener("load", resolve, { once: true });
      existing.addEventListener("error", resolve, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.dataset.loadState = "loading";
    script.onload = () => {
      script.dataset.loadState = "loaded";
      resolve();
    };
    script.onerror = () => {
      script.dataset.loadState = "error";
      resolve();
    };
    document.body.appendChild(script);
  });

let cartDrawerPromise;
let pendingCartDrawerPayload = null;

const connectedCartDrawer = () => {
  const cartDrawer = DutchXXLTheme.cartDrawer;
  return cartDrawer?.drawer?.isConnected ? cartDrawer : null;
};

const loadCartDrawer = () => {
  const cartDrawer = connectedCartDrawer();
  if (cartDrawer) return Promise.resolve(cartDrawer);

  cartDrawerPromise ||= loadScriptOnce(window.themeAssets?.cartDrawerJs);
  return cartDrawerPromise.then(() => {
    const loadedCartDrawer = connectedCartDrawer();
    if (loadedCartDrawer && pendingCartDrawerPayload) {
      const updatedFromSection = loadedCartDrawer.updateFromCartPayload(
        pendingCartDrawerPayload,
      );
      if (!updatedFromSection) loadedCartDrawer.load();
      pendingCartDrawerPayload = null;
    }
    return loadedCartDrawer;
  });
};

const cartToggleCount = (toggle) =>
  parseInt(toggle?.querySelector("[data-cart-count]")?.textContent || "", 10) ||
  parseInt($(".header__action-count[data-cart-count]")?.textContent || "", 10) ||
  0;

const bindDeferredCartDrawer = () => {
  // Visibility and off-canvas positioning remain in inline critical CSS. Only
  // behavior is deferred, so the server-rendered drawer cannot flash unstyled.
  const maybeLoad = (event) => {
    const target = event.target.closest?.(
      "[data-cart-toggle], .add-to-cart, [data-wishlist-add-to-cart]",
    );
    if (!target) return;
    if (target.matches("[data-cart-toggle]") && cartToggleCount(target) <= 0) {
      return;
    }
    loadCartDrawer();
  };

  document.addEventListener("focusin", maybeLoad);
  document.addEventListener("pointerover", maybeLoad, { passive: true });
  document.addEventListener("pointerdown", maybeLoad, { passive: true });

  document.addEventListener(
    "click",
    (event) => {
      const toggle = event.target.closest?.("[data-cart-toggle]");
      if (
        !toggle ||
        cartToggleCount(toggle) <= 0 ||
        connectedCartDrawer()
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      const href = toggle.href;
      const interaction = {
        type: "click",
        detail: event.detail,
      };

      loadCartDrawer().then((loadedCartDrawer) => {
        if (loadedCartDrawer && toggle.isConnected) {
          loadedCartDrawer.toggle(toggle, interaction);
        } else if (href) {
          window.location.assign(href);
        }
      });
    },
    true,
  );

  ["cart:item-added", "cart:quantity-changed"].forEach((eventName) => {
    addEventListener(eventName, (event) => {
      if (connectedCartDrawer()) return;
      if (event.detail) pendingCartDrawerPayload = event.detail;
      if (typeof event.detail?.item_count === "number") {
        syncCartCounts(event.detail);
      }
    });
  });

  addEventListener("cart:open-drawer", () => {
    if (connectedCartDrawer()) return;
    const trigger = document.activeElement;
    loadCartDrawer().then((loadedCartDrawer) => loadedCartDrawer?.open(trigger));
  });
};

let deferredCssPromise;
const ensureHeaderDeferredCss = () =>
  (deferredCssPromise ||= loadStylesheet(window.themeAssets?.headerDeferredCss));

let headerSearchPromise;
const loadHeaderSearch = () =>
  (headerSearchPromise ||= Promise.all([
    ensureHeaderDeferredCss(),
    loadScriptOnce(window.themeAssets?.headerSearchJs),
  ]));

const bindDeferredSearch = () => {
  const maybeLoad = (event) => {
    if (event.target.closest?.("[data-predictive-search], [data-mobile-search]")) {
      loadHeaderSearch();
    }
  };

  document.addEventListener("focusin", maybeLoad);
  document.addEventListener("pointerdown", maybeLoad, { passive: true });
};

// ─── StickyHeader ───────────────────────────────────────────────────────
class StickyHeader {
  constructor() {
    this.navWrapper = $(".nav-wrapper");
    this.stickyWrapper = $("[data-sticky-header]");
    if (!this.navWrapper && !this.stickyWrapper) return;

    this.mediaQuery = matchMedia("(min-width: 1200px)");
    addEventListener("scroll", this.onScroll, { passive: true });
  }

  onScroll = () => {
    const scrolled = scrollY > 50;
    if (this.mediaQuery.matches) {
      this.navWrapper?.classList.toggle("scrolled", scrolled);
      this.stickyWrapper?.classList.remove("scrolled");
    } else {
      this.stickyWrapper?.classList.toggle("scrolled", scrolled);
      this.navWrapper?.classList.remove("scrolled");
    }
  };
}

// ─── TrustBarMobile ─────────────────────────────────────────────────────
class TrustBarMobile {
  constructor() {
    const container = (this.container = $("[data-trust-mobile]"));
    if (!container) return;

    this.wrapper = container.closest(".trust-bar-wrapper");
    this.toggleBtn = $("[data-trust-toggle]", container);
    this.dropdown = $("[data-trust-dropdown]", container);
    this.iconEl = $("[data-trust-current-icon]", container);
    this.titleEl = $("[data-trust-current-title]", container);
    this.items = $$("[data-trust-dropdown-item]", container);
    this.currentIndex = 0;
    this.intervalId = null;
    this.mediaQuery = matchMedia("(max-width: 1199px)");
    this.motionQuery = matchMedia("(prefers-reduced-motion: reduce)");
    this.lastTitle = "";

    if (!this.items.length) return;

    this.set(0);

    this.toggleBtn?.addEventListener("click", () => this.tog());
    this.items.forEach((item, idx) =>
      item.addEventListener(
        "click",
        () => {
          this.set(idx);
          this.cls();
        },
      ),
    );
    if (this.mediaQuery.addEventListener) {
      this.mediaQuery.addEventListener("change", this.syncRotation);
    } else {
      this.mediaQuery.addListener?.(this.syncRotation);
    }
    if (this.motionQuery.addEventListener) {
      this.motionQuery.addEventListener("change", this.syncRotation);
    } else {
      this.motionQuery.addListener?.(this.syncRotation);
    }
    document.addEventListener("visibilitychange", this.syncRotation);
    addEventListener("pagehide", () => this.stop(), { once: true });
    this.syncRotation();
  }

  syncRotation = () => {
    if (!this.mediaQuery.matches) this.cls({ restart: false });
    this.shouldRotate() ? this.start() : this.stop();
  };

  shouldRotate() {
    return (
      this.items.length > 1 &&
      this.mediaQuery.matches &&
      !this.motionQuery.matches &&
      document.visibilityState === "visible" &&
      this.dropdown?.hasAttribute("hidden")
    );
  }

  start() {
    if (this.intervalId || !this.shouldRotate()) return;
    this.intervalId = setInterval(() => {
      if (!this.shouldRotate()) {
        this.stop();
        return;
      }
      this.currentIndex = (this.currentIndex + 1) % this.items.length;
      this.set(this.currentIndex);
    }, 3500);
  }

  stop() {
    if (!this.intervalId) return;
    clearInterval(this.intervalId);
    this.intervalId = null;
  }

  set(idx) {
    const item = this.items[idx];
    if (!item) return;

    this.currentIndex = idx;

    const { icon, title } = item.dataset;
    this.lastTitle = title || this.lastTitle;

    this.iconEl &&
      icon &&
      (this.iconEl.innerHTML = `<img src="${icon}" alt="" width="20" height="20" loading="lazy">`);
    this.lbl();
  }

  tog() {
    if (!this.dropdown || !this.toggleBtn) return;
    if (this.dropdown.hasAttribute("hidden")) {
      this.dropdown.removeAttribute("hidden");
      this.toggleBtn.setAttribute("aria-expanded", "true");
      this.wrapper?.classList.add("trust-bar--open");
      this.stop();
    } else {
      this.cls();
    }
    this.lbl();
  }

  cls({ restart = true } = {}) {
    if (!this.dropdown || !this.toggleBtn) return;
    this.dropdown.setAttribute("hidden", "true");
    this.toggleBtn.setAttribute("aria-expanded", "false");
    this.wrapper?.classList.remove("trust-bar--open");
    this.lbl();
    if (restart) this.syncRotation();
  }

  lbl() {
    if (!this.titleEl) return;
    const label = this.dropdown?.hasAttribute("hidden")
      ? this.lastTitle
      : "Inklappen";
    label && (this.titleEl.textContent = label);
  }
}

// ─── SearchExampleRotator ───────────────────────────────────────────────
class SearchExampleRotator {
  static INITIAL_DELAY_MS = 3000;
  static TYPE_DELAY_MS = 58;
  static DELETE_DELAY_MS = 38;
  static PHRASE_HOLD_MS = 2800;
  static PREFIX_HOLD_MS = 650;
  static CYCLE_REST_MS = 5200;

  constructor() {
    const input = (this.input = $("[data-search-animation]"));
    if (!input) return;

    const data = input.dataset.searchAnimation;
    if (!data) return;

    this.defaultPlaceholder = input.placeholder;
    this.phrases = this.parsePhrases(data);
    if (!this.phrases.length) return;

    this.phraseIndex = 0;
    this.currentPhrase = "";
    this.nextPhrase = "";
    this.nextPhraseIndex = 0;
    this.charIndex = 0;
    this.phase = "idle";
    this.prefixLength = 0;
    this.timerId = null;
    this.startTimerId = null;
    this.active = false;
    this.hasFocus = document.activeElement === input;
    this.hasWindowFocus =
      typeof document.hasFocus === "function" ? document.hasFocus() : true;
    this.runId = 0;
    this.motionQuery = matchMedia("(prefers-reduced-motion: reduce)");

    input.addEventListener("focus", this.onFocus);
    input.addEventListener("input", this.onInput);
    input.addEventListener("blur", this.onBlur);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    addEventListener("focus", this.onWindowFocus);
    addEventListener("blur", this.onWindowBlur);
    addEventListener("pagehide", this.onPageHide);
    addEventListener("pageshow", this.onPageShow);
    if (this.motionQuery.addEventListener) {
      this.motionQuery.addEventListener("change", this.onMotionChange);
    } else {
      this.motionQuery.addListener?.(this.onMotionChange);
    }

    this.resume({ delay: true });
  }

  normalizePhrase(phrase) {
    return phrase.replace(/\s+/g, " ").trim().toLowerCase();
  }

  parsePhrases(data) {
    const defaultKey = this.normalizePhrase(this.defaultPlaceholder);
    const seen = new Set();

    return data
      .split("|||")
      .map((phrase) => phrase.trim())
      .filter(Boolean)
      .filter((phrase) => {
        const key = this.normalizePhrase(phrase);
        if (!key || key === defaultKey || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }

  onFocus = () => {
    this.hasFocus = true;
    this.pause({ resetPlaceholder: true });
  };

  onInput = () => {
    if (this.input.value) {
      this.pause({ resetPlaceholder: true });
    } else {
      this.resume({ delay: true });
    }
  };

  onBlur = () => {
    this.hasFocus = false;
    this.resume({ delay: true });
  };

  onVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      this.resume({ delay: false });
    } else {
      this.pause({ resetPlaceholder: true });
    }
  };

  onWindowFocus = () => {
    this.hasWindowFocus = true;
    this.hasFocus = document.activeElement === this.input;
    this.resume({ delay: false });
  };

  onWindowBlur = () => {
    this.hasWindowFocus = false;
    this.pause({ resetPlaceholder: true });
  };

  onPageHide = () => {
    this.pause({ resetPlaceholder: true });
  };

  onPageShow = () => {
    this.hasWindowFocus =
      typeof document.hasFocus === "function" ? document.hasFocus() : true;
    this.hasFocus = document.activeElement === this.input;
    this.resume({ delay: false });
  };

  onMotionChange = () => {
    this.motionQuery.matches
      ? this.pause({ resetPlaceholder: true })
      : this.resume({ delay: true });
  };

  shouldAnimate() {
    return (
      !this.hasFocus &&
      this.hasWindowFocus &&
      this.input.isConnected &&
      !this.input.value &&
      !this.motionQuery.matches &&
      document.visibilityState === "visible"
    );
  }

  scheduleStart(delayMs = SearchExampleRotator.INITIAL_DELAY_MS) {
    if (this.active || this.startTimerId || !this.shouldAnimate()) return;
    const runId = this.runId;
    this.startTimerId = setTimeout(() => {
      if (runId !== this.runId) return;
      this.startTimerId = null;
      this.start();
    }, delayMs);
  }

  resume({ delay = false } = {}) {
    if (!this.shouldAnimate()) {
      this.pause({ resetPlaceholder: true });
      return;
    }

    if (this.active) return;

    clearTimeout(this.startTimerId);
    this.startTimerId = null;

    if (delay) {
      this.scheduleStart();
    } else {
      this.start();
    }
  }

  start() {
    if (!this.shouldAnimate() || this.active) return;
    const runId = ++this.runId;
    this.charIndex = 0;
    this.phraseIndex = 0;
    this.currentPhrase = this.phrases[0];
    this.prefixLength = 0;
    this.active = true;
    this.phase = "typing";
    this.input.placeholder = "";
    this.tick(runId);
  }

  pause({ resetPlaceholder = false } = {}) {
    this.runId++;
    this.active = false;
    clearTimeout(this.timerId);
    clearTimeout(this.startTimerId);
    this.timerId = null;
    this.startTimerId = null;
    if (resetPlaceholder) {
      this.input.placeholder = this.defaultPlaceholder;
    }
  }

  scheduleTick(runId, delayMs) {
    this.timerId = setTimeout(() => this.tick(runId), delayMs);
  }

  commonPrefixLength(a, b) {
    let idx = 0;
    while (idx < a.length && idx < b.length && a[idx] === b[idx]) idx++;
    return idx > 0 && a[idx - 1] === '"' ? idx - 1 : idx;
  }

  prepareDelete() {
    this.nextPhraseIndex = this.phraseIndex + 1;
    this.nextPhrase =
      this.nextPhraseIndex < this.phrases.length
        ? this.phrases[this.nextPhraseIndex]
        : "";
    this.prefixLength = this.nextPhrase
      ? this.commonPrefixLength(this.currentPhrase, this.nextPhrase)
      : 0;
    this.phase = "deleting";
  }

  finishCycle(runId) {
    if (runId !== this.runId) return;
    this.active = false;
    this.phase = "idle";
    this.input.placeholder = this.defaultPlaceholder;
    this.scheduleStart(SearchExampleRotator.CYCLE_REST_MS);
  }

  tick(runId) {
    if (runId !== this.runId) return;

    if (!this.shouldAnimate()) {
      this.pause({ resetPlaceholder: true });
      return;
    }

    if (this.phase === "deleting") {
      this.charIndex = Math.max(this.prefixLength, this.charIndex - 1);
      this.input.placeholder = this.currentPhrase.substring(0, this.charIndex);
      if (this.charIndex === this.prefixLength) {
        if (!this.nextPhrase) {
          this.finishCycle(runId);
          return;
        }

        this.phase = "prefix-hold";
        this.scheduleTick(runId, SearchExampleRotator.PREFIX_HOLD_MS);
        return;
      }

      this.scheduleTick(runId, SearchExampleRotator.DELETE_DELAY_MS);
      return;
    }

    if (this.phase === "prefix-hold") {
      this.phraseIndex = this.nextPhraseIndex;
      this.currentPhrase = this.nextPhrase;
      this.nextPhrase = "";
      this.phase = "typing";
      this.scheduleTick(runId, SearchExampleRotator.TYPE_DELAY_MS);
      return;
    }

    if (this.phase === "typing") {
      this.charIndex = Math.min(this.currentPhrase.length, this.charIndex + 1);
      this.input.placeholder = this.currentPhrase.substring(0, this.charIndex);
      if (this.charIndex === this.currentPhrase.length) {
        this.prepareDelete();
        this.scheduleTick(runId, SearchExampleRotator.PHRASE_HOLD_MS);
        return;
      }

      this.scheduleTick(runId, SearchExampleRotator.TYPE_DELAY_MS);
    }
  }
}

// ─── Clipboard helper ───────────────────────────────────────────────────
const copy = (txt, btn) =>
  navigator.clipboard.writeText(txt).then(() => {
    if (!btn) return;
    const copyIcon = $(".announcement-bar__copy-icon", btn);
    const checkIcon = $(".announcement-bar__check-icon", btn);
    if (copyIcon && checkIcon) {
      copyIcon.style.display = "none";
      checkIcon.style.display = "block";
    }
    setTimeout(() => {
      if (copyIcon && checkIcon) {
        copyIcon.style.display = "block";
        checkIcon.style.display = "none";
      }
    }, 2000);
  });

// ─── AnnouncementBar ────────────────────────────────────────────────────
class AnnouncementBar {
  constructor() {
    if (!$("[data-announcement-bar]")) return;
    const container = $("[data-copy-container]");
    const btn = $("[data-copy-code]");
    container?.addEventListener("click", (e) => {
      e.preventDefault();
      const text = $(".announcement-bar__code-text", container)?.textContent;
      text && copy(text, btn);
    });
  }
}

// ─── DesktopMegaMenus ──────────────────────────────────────────────────
class DesktopMegaMenus {
  constructor() {
    this.items = $$("[data-desktop-menu-item]");
    this.activeItem = null;
    if (!this.items.length) return;

    this.items.forEach((item) => {
      const trigger = this.trigger(item);
      const menu = this.menu(item);
      if (!trigger || !menu) return;

      item.addEventListener("mouseenter", () => this.open(item));
      item.addEventListener("mouseleave", () => {
        if (!item.contains(document.activeElement)) this.close(item);
      });
      item.addEventListener("focusout", (event) => {
        if (!item.contains(event.relatedTarget)) this.close(item);
      });

      trigger.addEventListener("click", (event) => {
        if (trigger.getAttribute("href") !== "#") return;
        event.preventDefault();
        this.open(item);
      });
      trigger.addEventListener("keydown", (event) =>
        this.onTriggerKeydown(event, item),
      );
      menu.addEventListener("keydown", (event) =>
        this.onMenuKeydown(event, item),
      );
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.activeItem) {
        const trigger = this.trigger(this.activeItem);
        this.close(this.activeItem);
        trigger?.focus();
      }
    });
  }

  trigger(item) {
    return $("[data-desktop-menu-trigger]", item);
  }

  menu(item) {
    return $("[data-desktop-mega-menu]", item);
  }

  open(item) {
    if (this.activeItem && this.activeItem !== item) this.close(this.activeItem);
    item.classList.add("nav__item--keyboard-open");
    this.trigger(item)?.setAttribute("aria-expanded", "true");
    this.activeItem = item;
  }

  close(item) {
    item.classList.remove("nav__item--keyboard-open");
    this.trigger(item)?.setAttribute("aria-expanded", "false");
    if (this.activeItem === item) this.activeItem = null;
  }

  firstMenuLink(item) {
    return $$(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      this.menu(item),
    ).find((el) => {
      const style = getComputedStyle(el);
      return style.visibility !== "hidden" && style.display !== "none";
    });
  }

  onTriggerKeydown(event, item) {
    if (event.key === "Escape") {
      event.preventDefault();
      this.close(item);
      this.trigger(item)?.focus();
      return;
    }

    if (
      event.key === " " ||
      event.key === "Spacebar" ||
      event.key === "Enter" ||
      event.key === "ArrowDown"
    ) {
      event.preventDefault();
      this.open(item);
      if (event.key === "ArrowDown") this.firstMenuLink(item)?.focus();
    }
  }

  onMenuKeydown(event, item) {
    if (event.key !== "Escape") return;
    event.preventDefault();
    this.close(item);
    this.trigger(item)?.focus();
  }
}

// ─── Initialization ─────────────────────────────────────────────────────
document.addEventListener("DOMContentLoaded", () => {
  initializeWishlistCount();
  bindDeferredCartDrawer();
  new StickyHeader();
  new SearchExampleRotator();
  new TrustBarMobile();
  new AnnouncementBar();
  new DesktopMegaMenus();

  let prevScrollY = 0,
    menuOpen = false,
    menuSwipeState = null;

  if (innerWidth < 1200) {
    const menu = $("[data-mobile-menu]");
    const menuOverlay = $("[data-mobile-menu-overlay]");
    menu && (menu.style.display = "");
    menuOverlay && (menuOverlay.style.display = "");
  }

  const SW = {
    LOCK: 10,
    MIN_DISTANCE: 75,
    MAX_VERTICAL: 100,
    RATIO: 1.35,
    TIMEOUT: 300,
    CLOSE_RATIO: 0.28,
    FLICK_DISTANCE: 56,
  };

  const toggleBtn = $("[data-mobile-menu-toggle]");
  const mobileMenu = $("[data-mobile-menu]");
  const closeBtn = $("[data-mobile-menu-close]");
  const overlay = $("[data-mobile-menu-overlay]");
  const submenuToggles = $$("[data-submenu-toggle]");
  const miniCart = $("[data-mini-cart]");
  const miniCartItems = $("[data-mini-cart-items]");
  const cartFooter = $("[data-cart-footer]");
  const cartWrapper = $("[data-cart-wrapper]");

  const visibleFocusable = (node) => {
    if (!node || node.disabled || node.closest('[aria-hidden="true"]')) {
      return false;
    }

    const hiddenParent = node.closest("[hidden]");
    if (hiddenParent) return false;

    const style = getComputedStyle(node);
    if (style.display === "none" || style.visibility === "hidden") {
      return false;
    }

    return Boolean(node.offsetWidth || node.offsetHeight || node.getClientRects().length);
  };

  const resetMenuScroll = () => {
    if (!mobileMenu) return;
    mobileMenu.scrollTop = 0;
    $$("[data-panel], .mobile-nav-panel__content, [data-mobile-search-results]", mobileMenu).forEach(
      (scrollEl) => {
        scrollEl.scrollTop = 0;
      },
    );
  };

  const clearMenuSwipeStyles = () => {
    if (!mobileMenu) return;
    mobileMenu.classList.remove("custom-mobile-menu--swiping");
    mobileMenu.style.removeProperty("--mobile-menu-swipe-x");
  };

  const isMenuSwipeIgnored = (target) => Boolean(
    target.closest?.('[data-mobile-search], input, select, textarea, [contenteditable="true"]'),
  );

  const openMenu = () => {
    if (menuOpen || !mobileMenu) return;
    const deferredCssReady = ensureHeaderDeferredCss();
    clearMenuSwipeStyles();
    menuOpen = true;
    prevScrollY = scrollY;
    Object.assign(document.body.style, {
      overflow: "hidden",
      position: "fixed",
      top: `-${prevScrollY}px`,
      width: "100%",
    });
    toggleBtn?.setAttribute("aria-expanded", "true");
    mobileMenu.classList.add("active");
    overlay?.classList.add("active");
    closeBtn?.focus();
    mobileMenu.addEventListener("keydown", trapFocus);
    document.addEventListener("keydown", handleEscapeKey);
    deferredCssReady.then(loadMiniCart);
  };

  let closeMenu = (swipe = false) => {
    if (!menuOpen || !mobileMenu) return;
    menuOpen = false;
    menuSwipeState = null;
    clearMenuSwipeStyles();
    toggleBtn?.setAttribute("aria-expanded", "false");
    mobileMenu.classList.remove("active");
    overlay?.classList.remove("active");
    mobileMenu.style.transform = "";
    const scrollPos = scrollTopFromBodyLock(prevScrollY);
    Object.assign(document.body.style, {
      overflow: "",
      position: "",
      top: "",
      width: "",
    });
    restoreViewportScroll(scrollPos);
    swipe ? toggleBtn?.blur() : toggleBtn?.focus({ preventScroll: true });
    mobileMenu.removeEventListener("keydown", trapFocus);
    document.removeEventListener("keydown", handleEscapeKey);
    mobileMenu.dispatchEvent(
      new CustomEvent("header:mobile-menu-close", { bubbles: true }),
    );
    resetMenuScroll();
    $$(".custom-mobile-menu__item.expanded").forEach((item) => {
      item.classList.remove("expanded");
      const link = $(".custom-mobile-menu__link.has-submenu", item);
      const toggle = $("[data-submenu-toggle]", item);
      link?.classList.remove("sticky-active");
      toggle?.setAttribute("aria-expanded", "false");
      (link?.nextElementSibling ?? toggle?.nextElementSibling)?.setAttribute(
        "aria-hidden",
        "true",
      );
    });
  };

  const handleEscapeKey = (e) => e.key === "Escape" && menuOpen && closeMenu();

  const handleTouchStart = (e) => {
    if (!menuOpen || !mobileMenu || e.touches.length !== 1) {
      menuSwipeState = null;
      return;
    }

    const touch = e.touches[0];
    menuSwipeState = {
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: performance.now(),
      width: mobileMenu.offsetWidth || innerWidth,
      lock: null,
      ignore: isMenuSwipeIgnored(e.target),
    };
  };

  const handleTouchMove = (e) => {
    const state = menuSwipeState;
    if (!state || !menuOpen || !mobileMenu || e.touches.length !== 1) return;

    const touch = e.touches[0];
    const dx = state.startX - touch.clientX;
    const dy = touch.clientY - state.startY;
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);

    if (!state.lock) {
      if (Math.max(absX, absY) < SW.LOCK) return;
      state.lock = absX > absY * SW.RATIO ? "x" : "y";
    }

    if (state.lock !== "x") return;
    e.preventDefault();

    if (dx <= 0 || state.ignore) {
      clearMenuSwipeStyles();
      return;
    }

    const dragX = Math.min(dx, state.width);
    mobileMenu.classList.add("custom-mobile-menu--swiping");
    mobileMenu.style.setProperty("--mobile-menu-swipe-x", `-${dragX}px`);
  };

  const handleTouchEnd = (e) => {
    const state = menuSwipeState;
    menuSwipeState = null;
    if (!state || !mobileMenu) {
      clearMenuSwipeStyles();
      return;
    }

    const touch = e.changedTouches[0];
    const dx = state.startX - touch.clientX;
    const dy = Math.abs(touch.clientY - state.startY);
    const dt = performance.now() - state.startTime;
    const distanceThreshold = Math.max(SW.MIN_DISTANCE, state.width * SW.CLOSE_RATIO);
    const distanceClose =
      dx > distanceThreshold &&
      dy < SW.MAX_VERTICAL &&
      dx > dy * SW.RATIO;
    const flickClose =
      dx > SW.FLICK_DISTANCE &&
      dx > dy * SW.RATIO &&
      dt < SW.TIMEOUT;

    if (state.lock === "x" && dx > 0 && !state.ignore && (distanceClose || flickClose)) {
      requestAnimationFrame(() => closeMenu(true));
      return;
    }

    clearMenuSwipeStyles();
  };

  const handleTouchCancel = () => {
    menuSwipeState = null;
    clearMenuSwipeStyles();
  };

  const trapFocus = (e) => {
    if (e.key !== "Tab") return;
    const focusable = $$(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      mobileMenu,
    ).filter(visibleFocusable);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      last.focus();
      e.preventDefault();
    } else if (!e.shiftKey && document.activeElement === last) {
      first.focus();
      e.preventDefault();
    }
  };

  function toggleSubmenu(e) {
    e.preventDefault();
    const item = this.closest(".custom-mobile-menu__item");
    const link = this.closest(".custom-mobile-menu__link.has-submenu");
    const expanded = item.classList.contains("expanded");
    const submenu = link?.nextElementSibling ?? this.nextElementSibling;

    $$(".custom-mobile-menu__item.expanded").forEach((other) => {
      if (other === item) return;
      other.classList.remove("expanded");
      const otherLink = $(".custom-mobile-menu__link.has-submenu", other);
      const otherToggle = $("[data-submenu-toggle]", other);
      otherLink?.classList.remove("sticky-active");
      otherToggle?.setAttribute("aria-expanded", "false");
      (
        otherLink?.nextElementSibling ?? otherToggle?.nextElementSibling
      )?.setAttribute("aria-hidden", "true");
    });

    requestAnimationFrame(() => {
      item.classList.toggle("expanded", !expanded);
      link?.classList.toggle("sticky-active", !expanded);
      this.setAttribute("aria-expanded", String(!expanded));
      submenu?.setAttribute("aria-hidden", String(expanded));
      expanded && mobileMenu.scrollTo({ top: 0, behavior: "smooth" });
    });
  }

  // ─── Mini cart in mobile menu ───────────────────────────────────────
  const loadMiniCart = () => {
    if (!miniCart) return;
    miniCart.setAttribute("aria-busy", "true");
    fetchCart()
      .then(updateMiniCart)
      .catch(() => {})
      .finally(() => miniCart.setAttribute("aria-busy", "false"));
  };

  ["cart:item-added", "cart:quantity-changed"].forEach((ev) =>
    addEventListener(ev, (event) => {
      if (!menuOpen) return;
      const cart = event.detail;
      if (cart?.items && typeof cart.item_count === "number") {
        updateMiniCart(cart);
      } else {
        loadMiniCart();
      }
    }),
  );

  const updateMiniCart = (cart) => {
    if (!miniCartItems || !cart) return;
    syncCartCounts(cart);
    cartWrapper && (cartWrapper.hidden = cart.item_count === 0);
    if (cart.item_count > 0) {
      cartFooter && (cartFooter.hidden = false);
      $$("[data-cart-subtotal]").forEach(
        (subtotalEl) => (subtotalEl.textContent = fmt(cart.total_price)),
      );
      const totalPriceEl = $(".custom-mobile-menu__total-price");
      totalPriceEl && (totalPriceEl.textContent = fmt(cart.total_price));
      miniCartItems.innerHTML = "";
      cart.items.forEach((item) =>
        miniCartItems.appendChild(createMiniCartItem(item)),
      );
    } else {
      cartFooter && (cartFooter.hidden = true);
      miniCartItems.innerHTML = "";
    }
  };

  const createMiniCartItem = (item) => {
    const itemPrice = Number.isFinite(item.final_price) ? item.final_price : item.price;
    const title =
      item.title?.replace(` - ${item.variant_title}`, "") || item.title;
    const safeTitle = escapeHtml(title);
    const safeVariantTitle = escapeHtml(item.variant_title || "");
    const imageMarkup = item.image
      ? `<img src="${escapeHtml(item.image)}" alt="${safeTitle}" loading="lazy" width="70" height="105">`
      : "";
    const node = el("div", "custom-mobile-menu__mini-cart-item");
    node.innerHTML = `<div class="custom-mobile-menu__mini-cart-item-image">
  ${imageMarkup}
</div>
<div class="custom-mobile-menu__mini-cart-item-details">
  <div>
    <h5 class="custom-mobile-menu__mini-cart-item-title">${safeTitle}</h5>
    ${safeVariantTitle ? `<div class="custom-mobile-menu__mini-cart-item-variant">${safeVariantTitle}</div>` : ""}
  </div>
  <div>
    <div class="custom-mobile-menu__mini-cart-item-price">${fmt(itemPrice)}<span class="custom-mobile-menu__mini-cart-item-quantity"> × ${item.quantity}</span></div>
    <button type="button" class="custom-mobile-menu__mini-cart-item-remove"
      data-cart-remove="${escapeHtml(item.variant_id)}" data-key="${escapeHtml(item.key)}"
      aria-label="Verwijder item">×</button>
  </div>
</div>`;
    return node;
  };

  miniCartItems?.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-cart-remove]");
    if (btn) {
      e.preventDefault();
      removeMiniCartItem(btn);
    }
  });

  const removeMiniCartItem = (btn) => {
    const key = btn?.dataset.key;
    if (!key || btn.disabled) return;
    const originalText = btn.textContent;
    const itemNode = btn.closest(".custom-mobile-menu__mini-cart-item");
    btn.textContent = "...";
    btn.disabled = true;
    btn.setAttribute("aria-busy", "true");
    itemNode?.setAttribute("aria-busy", "true");
    postCart("change.js", { id: key, quantity: 0 })
      .then((cart) => {
        updateMiniCart(cart);
        dispatchEvent(new CustomEvent("cart:quantity-changed", { detail: cart }));
      })
      .catch(() => {
        btn.textContent = originalText || "×";
        btn.disabled = false;
        btn.setAttribute("aria-busy", "false");
        itemNode?.setAttribute("aria-busy", "false");
      });
  };



  if (mobileMenu) {
    toggleBtn?.addEventListener("pointerdown", ensureHeaderDeferredCss, {
      passive: true,
    });
    toggleBtn?.addEventListener("focusin", ensureHeaderDeferredCss);
    toggleBtn?.addEventListener("click", openMenu);
    overlay?.addEventListener("click", closeMenu);
    mobileMenu.addEventListener("touchstart", handleTouchStart, {
      passive: true,
      capture: true,
    });
    mobileMenu.addEventListener("touchmove", handleTouchMove, {
      passive: false,
      capture: true,
    });
    mobileMenu.addEventListener("touchend", handleTouchEnd, {
      passive: true,
      capture: true,
    });
    mobileMenu.addEventListener("touchcancel", handleTouchCancel, {
      passive: true,
      capture: true,
    });
    submenuToggles.forEach((toggle) =>
      toggle.addEventListener("click", toggleSubmenu),
    );
    mobileMenu.addEventListener("click", (e) => {
      if (e.target.closest("[data-mobile-menu-close]")) {
        closeMenu();
        return;
      }
      if (!e.target.matches('a[href^="#"]')) return;
      e.preventDefault();
      const target = $(e.target.getAttribute("href"));
      if (!target) return;
      closeMenu();
      afterElementMotion(mobileMenu, () => {
        target.scrollIntoView({ behavior: "smooth" });
        target.setAttribute("tabindex", "-1");
        target.focus({ preventScroll: true });
      });
    });
  }

  bindDeferredSearch();

  // ─── NavStack: Drill-down panel navigation ──────────────────────────
  class NavStack {
    constructor(rootEl) {
      this.el = rootEl;
      if (!rootEl) return;

      this.menu = rootEl.closest("[data-mobile-menu]");
      this.stack = ["root"];
      this.panels = $$("[data-panel]", rootEl);
      this.triggers = new Map();
      this.currentTrigger = null;
      this.touchStartX = null;
      this.touchStartY = null;
      this.touchStartTime = 0;
      this.touchLock = null;

      rootEl.addEventListener("click", (e) => {
        const push = e.target.closest("[data-nav-push]");
        if (push) {
          this.push(push.dataset.navPush, push);
          return;
        }
        if (e.target.closest("[data-nav-back]")) this.pop();
      });

      rootEl.addEventListener("touchstart", this.handleTouchStart, {
        passive: true,
      });
      rootEl.addEventListener("touchmove", this.handleTouchMove, {
        passive: false,
      });
      rootEl.addEventListener("touchend", this.handleTouchEnd, {
        passive: true,
      });
      rootEl.addEventListener("touchcancel", this.handleTouchCancel, {
        passive: true,
      });
    }

    esc(value) {
      const div = document.createElement("div");
      div.textContent = value || "";
      return div.innerHTML;
    }

    createPanel(handle, trigger) {
      const data = $(`[data-nav-panel-data="${handle}"]`, this.el);
      if (!data) return null;

      let sections = [];
      try {
        sections = JSON.parse(data.textContent || "[]");
      } catch {
        return null;
      }

      const title = this.esc(data.dataset.navPanelTitle || "");
      const panel = document.createElement("div");
      panel.className = "mobile-nav-panel";
      panel.dataset.panel = handle;
      panel.id = trigger?.getAttribute("aria-controls") || `MobileNavPanel-${handle}`;

      const content = sections
        .map((section) => {
          const links = Array.isArray(section.links) ? section.links : [];
          if (links.length) {
            return `<div class="mobile-nav-panel__section">
              <h3 class="mobile-nav-panel__section-title">${this.esc(section.title)}</h3>
              <ul class="mobile-nav-panel__list">
                ${links
                  .map(
                    (link) =>
                      `<li><a href="${this.esc(link.url)}" class="mobile-nav-panel__link">${this.esc(link.title)}</a></li>`,
                  )
                  .join("")}
              </ul>
            </div>`;
          }

          if (!section.url) return "";
          return `<div class="mobile-nav-panel__section">
            <ul class="mobile-nav-panel__list">
              <li><a href="${this.esc(section.url)}" class="mobile-nav-panel__link">${this.esc(section.title)}</a></li>
            </ul>
          </div>`;
        })
        .join("");

      panel.innerHTML = `<div class="mobile-nav-panel__header">
        <button type="button" class="mobile-nav-panel__back" data-nav-back>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="9,18 15,12 9,6"/>
          </svg>
          Terug
        </button>
        <span class="mobile-nav-panel__title">${title}</span>
        <button type="button" class="mobile-nav-panel__close" data-mobile-menu-close aria-label="Menu sluiten">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
      <div class="mobile-nav-panel__content">${content}</div>
      <button type="button" class="mobile-nav-panel__fab" data-nav-back aria-label="Terug">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>`;

      this.el.appendChild(panel);
      this.panels = $$("[data-panel]", this.el);
      return panel;
    }

    updateState() {
      this.menu?.classList.toggle("is-subpanel", this.stack.length > 1);
    }

    focusPanel(panel) {
      requestAnimationFrame(() => {
        const target =
          $("[data-nav-back]", panel) ||
          $('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])', panel);
        target?.focus({ preventScroll: true });
      });
    }

    resetPanelScroll(panel) {
      if (!panel) return;
      panel.scrollTop = 0;
      $$(".mobile-nav-panel__content", panel).forEach((content) => {
        content.scrollTop = 0;
      });
    }

    push(handle, trigger) {
      const current = $("[data-panel].mobile-nav-panel--active", this.el);
      const next =
        $(`[data-panel="${handle}"]`, this.el) || this.createPanel(handle, trigger);
      if (!current || !next) return;

      this.currentTrigger?.setAttribute("aria-expanded", "false");
      if (trigger) {
        trigger.setAttribute("aria-expanded", "true");
        this.triggers.set(handle, trigger);
        this.currentTrigger = trigger;
      }
      this.resetPanelScroll(next);
      current.classList.remove("mobile-nav-panel--active");
      current.classList.add("mobile-nav-panel--hidden-left");
      next.classList.remove("mobile-nav-panel--hidden-left");
      next.classList.add("mobile-nav-panel--active");
      this.stack.push(handle);
      this.updateState();
      this.focusPanel(next);
    }

    pop() {
      if (this.stack.length <= 1) return;
      const currentHandle = this.stack.pop();

      const current = $("[data-panel].mobile-nav-panel--active", this.el);
      const prevHandle = this.stack[this.stack.length - 1];
      const prev = $(`[data-panel="${prevHandle}"]`, this.el);
      if (!current || !prev) return;

      const returnTrigger = this.triggers.get(currentHandle);
      returnTrigger?.setAttribute("aria-expanded", "false");
      this.currentTrigger = prevHandle === "root" ? null : this.triggers.get(prevHandle);
      this.resetPanelScroll(current);
      current.classList.remove("mobile-nav-panel--active");
      current.classList.add("mobile-nav-panel");
      prev.classList.remove("mobile-nav-panel--hidden-left");
      prev.classList.add("mobile-nav-panel--active");
      this.updateState();
      requestAnimationFrame(() => returnTrigger?.focus({ preventScroll: true }));
    }

    reset() {
      this.panels.forEach((panel) => {
        panel.classList.remove(
          "mobile-nav-panel--active",
          "mobile-nav-panel--hidden-left",
        );
        if (panel.dataset.panel === "root")
          panel.classList.add("mobile-nav-panel--active");
        this.resetPanelScroll(panel);
      });
      $$("[data-nav-push]", this.el).forEach((push) =>
        push.setAttribute("aria-expanded", "false"),
      );
      this.stack = ["root"];
      this.currentTrigger = null;
      this.updateState();
    }

    handleTouchStart = (e) => {
      e.stopPropagation();
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
      this.touchStartTime = performance.now();
      this.touchLock = null;
    };

    handleTouchMove = (e) => {
      if (this.touchStartX === null || this.touchStartY === null || this.stack.length <= 1) return;
      e.stopPropagation();

      const dx = e.touches[0].clientX - this.touchStartX;
      const dy = e.touches[0].clientY - this.touchStartY;
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);

      if (!this.touchLock) {
        if (Math.max(absX, absY) < SW.LOCK) return;
        this.touchLock = absX > absY * SW.RATIO ? "x" : "y";
      }

      if (this.touchLock === "x" && dx > 0) e.preventDefault();
    };

    handleTouchEnd = (e) => {
      e.stopPropagation();
      if (this.touchStartX === null || this.touchStartY === null || this.stack.length <= 1) {
        this.handleTouchCancel(e);
        return;
      }

      const dx = e.changedTouches[0].clientX - this.touchStartX;
      const dy = Math.abs(e.changedTouches[0].clientY - this.touchStartY);
      const dt = performance.now() - this.touchStartTime;

      if (this.touchLock === "x" && dx > 60 && dx > dy * SW.RATIO && dt < SW.TIMEOUT) this.pop();
      this.handleTouchCancel(e);
    };

    handleTouchCancel = (e) => {
      e?.stopPropagation?.();
      this.touchStartX = null;
      this.touchStartY = null;
      this.touchStartTime = 0;
      this.touchLock = null;
    };
  }

  let navStack = new NavStack($("[data-nav-stack]"));

  // Wrap closeMenu to also reset NavStack
  const origCloseMenu = closeMenu;
  closeMenu = (swipe) => {
    origCloseMenu(swipe);
    if (navStack?.el) navStack.reset();
  };

  // Reinitialize NavStack when Shopify Theme Editor reloads the header section
  document.addEventListener("shopify:section:load", (e) => {
    if (e.target.querySelector("[data-mobile-menu]")) {
      navStack = new NavStack($("[data-nav-stack]"));
    }
  });
});
})();
